// Generado desde design-system/brand/DbLogoMotion.tsx — no editar; edita el .ts/.tsx y corre build-ds.
import { useCallback, useEffect, useRef, useState } from 'react';
const INTRO_KEY = 'db-logo-intro';
const REPLAY_RATE = 1.25; // los momentos de respuesta se sienten como respuesta, no como espera
const COOLDOWN_MS = 600;
const visible = (el) => !!el && el.getClientRects().length > 0;
/**
 * Logotipo animado de Data Bunker (video con fondo transparente, ~150 KB).
 *
 * Movimiento con causa: el logo descansa armado y quieto (primer y último cuadro del video = logotipo
 * completo, el paso entre video y póster no se nota) y solo se anima cuando significa algo. Un bucle
 * constante en la periferia es ruido visual.
 *
 * Bajo consumo: fuera de esos momentos no hay <video> en el DOM (solo el póster). Un archivo por navegador
 * (WebM VP9 con alfa o HEVC con alfa en Safari/iOS), pausa con la pestaña oculta o fuera de pantalla, y con
 * «reducir movimiento» o ahorro de datos nunca descarga el video.
 */
export function DbLogoMotion({ height = 96, variant = 'color', size = 'sm', playback = 'moments', animated = true, replayOnHover = true, idleReplayMs = 30 * 60 * 1000, introOncePerSession = true, basePath = '/brand/logo-motion/', title = 'Data Bunker', className, style, }) {
    const mode = animated ? playback : 'static';
    const hostRef = useRef(null);
    const videoRef = useRef(null);
    const rate = useRef(1);
    const lastEnd = useRef(0);
    const [canAnimate, setCanAnimate] = useState(false);
    const [src, setSrc] = useState(null);
    const [playing, setPlaying] = useState(false);
    const poster = `${basePath}logo-motion-poster.png`;
    // ¿Se permite el movimiento? (cliente: reducir movimiento, ahorro de datos) y qué archivo usar.
    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const saveData = navigator.connection?.saveData === true;
        const update = () => setCanAnimate(mode !== 'static' && !mq.matches && !saveData);
        update();
        mq.addEventListener('change', update);
        const ua = navigator.userAgent;
        const webkitOnly = /iPad|iPhone|iPod/.test(ua) || (/AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg|OPR|Firefox/.test(ua));
        setSrc(`${basePath}logo-motion-${size}.${webkitOnly ? 'mov' : 'webm'}`);
        return () => mq.removeEventListener('change', update);
    }, [mode, basePath, size]);
    // Reproduce UNA vez (moments). No reinicia si ya corre; respeta una pausa corta tras terminar.
    const playOnce = useCallback((r) => {
        if (!canAnimate || mode !== 'moments' || playing)
            return;
        if (Date.now() - lastEnd.current < COOLDOWN_MS)
            return;
        rate.current = r;
        setPlaying(true);
    }, [canAnimate, mode, playing]);
    // Bucle: montado mientras se permita el movimiento.
    useEffect(() => {
        if (mode === 'loop')
            setPlaying(canAnimate);
        if (!canAnimate)
            setPlaying(false);
    }, [mode, canAnimate]);
    // 1) Bienvenida: una vez por sesión, solo en la instancia que se ve (sidebar y barra móvil comparten la marca).
    useEffect(() => {
        if (!canAnimate || mode !== 'moments' || !visible(hostRef.current))
            return;
        let seen = false;
        try {
            seen = introOncePerSession && sessionStorage.getItem(INTRO_KEY) === '1';
            sessionStorage.setItem(INTRO_KEY, '1');
        }
        catch {
            /* sin almacenamiento: la bienvenida se ve en cada carga */
        }
        if (!seen) {
            rate.current = 1;
            setPlaying(true);
        }
    }, [canAnimate, mode, introOncePerSession]);
    // 2) Respuesta al gesto: mouse o foco sobre la marca (el enlace/botón que contiene al logo, o el propio logo).
    useEffect(() => {
        const host = hostRef.current;
        if (!host || !replayOnHover || !canAnimate || mode !== 'moments')
            return undefined;
        const trigger = host.closest('a, button') ?? host;
        const onEnter = () => playOnce(REPLAY_RATE);
        trigger.addEventListener('pointerenter', onEnter);
        trigger.addEventListener('focusin', onEnter);
        return () => {
            trigger.removeEventListener('pointerenter', onEnter);
            trigger.removeEventListener('focusin', onEnter);
        };
    }, [replayOnHover, canAnimate, mode, playOnce]);
    // 3) Regreso tras una ausencia larga (pestaña oculta más de idleReplayMs).
    useEffect(() => {
        if (!idleReplayMs || !canAnimate || mode !== 'moments')
            return undefined;
        let hiddenAt = document.hidden ? Date.now() : 0;
        const onVis = () => {
            if (document.hidden)
                hiddenAt = Date.now();
            else if (hiddenAt && Date.now() - hiddenAt >= idleReplayMs && visible(hostRef.current))
                playOnce(REPLAY_RATE);
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [idleReplayMs, canAnimate, mode, playOnce]);
    // Mientras hay video: arrancar, y pausar con la pestaña oculta o fuera de pantalla.
    useEffect(() => {
        const v = videoRef.current;
        if (!v || !playing)
            return undefined;
        v.muted = true; // la propiedad, no solo el atributo: requisito de autoplay
        v.playbackRate = mode === 'loop' ? 1 : rate.current;
        let onScreen = true;
        const sync = () => {
            if (document.hidden || !onScreen)
                v.pause();
            else
                v.play().catch(() => setPlaying(false));
        };
        const io = new IntersectionObserver(([e]) => {
            onScreen = e.isIntersecting;
            sync();
        });
        io.observe(v);
        document.addEventListener('visibilitychange', sync);
        sync();
        return () => {
            io.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, [playing, mode, src]);
    const media = {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        filter: variant === 'mono' ? 'brightness(0) invert(1)' : undefined,
    };
    return (<span ref={hostRef} role="img" aria-label={title} className={className} style={{ display: 'inline-block', height, aspectRatio: '4 / 3', flexShrink: 0, ...style }}>
      {playing && src ? (<video ref={videoRef} src={src} poster={poster} muted loop={mode === 'loop'} playsInline preload="auto" disablePictureInPicture disableRemotePlayback aria-hidden="true" tabIndex={-1} onEnded={() => {
                lastEnd.current = Date.now();
                setPlaying(false);
            }} onError={() => {
                setPlaying(false);
                setCanAnimate(false);
            }} style={media}/>) : (<img src={poster} alt="" aria-hidden="true" decoding="async" style={media}/>)}
    </span>);
}
